<script setup lang="ts">
import {
  AppContext,
  apps,
  BackendApi, legacyModeKey,
  LegacyOrder,
  LegacyOrderDraftChanges,
  log,
  NavHubSection,
  Portal,
  ServiceContext,
  useContext,
  useDialogs,
  useHost,
  useProcesses,
  useResolveLabel,
  WorkspaceMenuItem
} from '@hantera/portal-app';

import { Component, inject, provide } from 'vue';
import { appsKey, viewRepositoryKey } from './keys';
import useBackend from './composition/useBackend';
import { AppComponent } from './composition/use-apps';
import { useRouter } from "vue-router";
import AppViewContext from "@/AppViewContext.vue";

const host = useHost()
const processes = useProcesses()

const backend = useBackend();
const portalContext = useContext();
const legacyMode = inject(legacyModeKey, false);
const resolveLabel = useResolveLabel()

const components: { [area: string]: AppComponent[] } = {};
const services: { [type: string]: any[] } = {};
const navHubSections: NavHubSection[] = []

const router = useRouter()
let localDevAppId: string | undefined = undefined;

const onLegacyOrderSaved: ((order: LegacyOrder, changes: LegacyOrderDraftChanges[]) => void)[] = [];

const hooks = {
  onLegacyOrderSaved(callback: (order: LegacyOrder, changes: LegacyOrderDraftChanges[]) => void) {
    onLegacyOrderSaved.push(callback);
  }
};

const viewRepository = inject(viewRepositoryKey, {} as any)

const dialogs = useDialogs()

const serviceContext = <ServiceContext>{
  backend: backend as BackendApi,
  processes,
  dialogs,
  localization: {
    resolveLabel: resolveLabel.resolve
  }
}


// Load local development app (if activated):
if ((<any>window).__HANTERA_APP_DEVELOPMENT__) {

  const proc = processes.createProcess({ description: 'Loading local app development' });
  try {
    const app: any = await import(/* @vite-ignore */`${'https://localhost:4734/loader.ts'}`);
    localDevAppId = app.appId.toString();

    const appContext = <AppContext>{
      appId: localDevAppId,
      getAppViewPath(relativePath: string, appId?: string): string {
        if (!appId) {
          return `/apps/${localDevAppId}/${relativePath}`
        }

        return `/apps/${appId}/${relativePath}`
      },
      getServices<T>(type: apps.AppService<T>) {
        return (services[type.key] ?? []) as T[]
      }
    }

    const portal: Portal & { legacyMode: boolean } = {
      context: portalContext,
      legacyMode, // Hidden field for backward compatiblity
      registerComponent<T>(area: apps.AppSlot<T>, component: Component) {
        if (!components[area.key]) {
          components[area.key] = [];
        }
        log.info('Registering local development component: ', app.appId.toString(), area, component);
        components[area.key].push({
          key: Symbol('localdev'),
          appContext,
          component
        });
      },
      registerService<T>(type: apps.AppService<T>, serviceFactory: (serviceContext: ServiceContext) => T) {
        if (!services[type.key]) {
          services[type.key] = [];
        }

        log.info(`Registering local development service: `, app.appId.toString(), type.key, serviceFactory(serviceContext));
        services[type.key].push(serviceFactory(serviceContext));
      },
      registerView(path: string, component: Component, accessPatterns?: string[]) {

        log.info(`Registering local development view: `, app.appId.toString(), path, component);
        const fullPath = `/apps/${localDevAppId}/${path}`
        viewRepository.registerView(
          fullPath,
          AppViewContext,
          accessPatterns,
          {
            appContext,
            component
          }
        )
        return {
          fullPath
        }
      },
      registerWorkspaceMenuItem(item: WorkspaceMenuItem) {
        log.info(`Registering local development workspace menu item: `, app.appId.toString(), item.folderPath, item.label);
        navHubSections.push({
          id: item.folderPath,
          items: [{
            label: item.label,
            action: context => {
              item.action({
                ...context,
                open(viewRoute: string, label: string) {
                  context.openView(viewRoute, {})
                }
              })
            },
            accessPatterns: item.accessPatterns
          }]
        });
      },
      registerNavHubSection(section: NavHubSection) {
        log.info(`Registering local development NavHub section: `, app.appId.toString(), section.id, section.items.map(r => r.label));
        navHubSections.push(section)
      },
      ...hooks
    };

    app.default(portal);
    proc.complete();
  } catch (error) {
    proc.fail({ message: `Unable to enable local development. Did you forget to run 'h_ app dev'? Error: ${error}` });
  }
}

// Load installed apps
const res = await backend.fetch('/apps', {
  method: 'GET',
});

if (res.ok) {
  for (const app of await res.json() as [{
    id: string
    version: string
    files: {
      contentType: 'application/javascript' | 'text/css'
      path: string
    }[]
  }]) {
    if (app.id === localDevAppId) {
      log.warn(`Skipping loading of installed app ${app.id}@${app.version} because it is the local development app`);
      continue;
    }

    let failed = false;

    for (const file of app.files) {

      const fileResponse = await backend.fetch(`/apps/file?appId=${encodeURIComponent(app.id)}&path=${encodeURIComponent(file.path)}`, {
        method: 'GET',
      });

      if (!fileResponse.ok) {
        log.err(`Unable to load app '${app.id}@${app.version}/${file.path}' file: `, res.status, res.statusText);
        failed = true;
        break;
      }

      if (file.contentType === 'text/css') {
        const styleElement = document.createElement('style');
        styleElement.setAttribute('type', 'text/css');
        styleElement.setAttribute('app-id', app.id);
        styleElement.setAttribute('app-version', app.version);
        styleElement.appendChild(document.createTextNode(await fileResponse.text()));
        document.head.appendChild(styleElement);
      } else if (file.contentType === 'application/javascript') {

        const appContext = <AppContext>{
          appId: app.id,
          getAppViewPath(relativePath: string, appId?: string): string {
            if (!appId) {
              return `/apps/${app.id}/${relativePath}`
            }

            return `/apps/${appId}/${relativePath}`
          },
          getServices<T>(type: apps.AppService<T>) {
            return (services[type.key] ?? []) as T[]
          }
        }

        const portal: Portal = {
          context: portalContext,
          registerComponent<T>(area: apps.AppSlot<T>, component: Component) {
            if (!components[area.key]) {
              components[area.key] = [];
            }

            log.info(`Registering '${app.id}@${app.version}' component: `, area.key, component);
            components[area.key].push({
              key: Symbol(app.id),
              appContext,
              component
            });
          },
          registerService<T>(type: apps.AppService<T>, serviceFactory: (serviceContext: ServiceContext) => T) {
            if (!services[type.key]) {
              services[type.key] = [];
            }

            log.info(`Registering '${app.id}@${app.version}' service: `, type.key, serviceFactory(serviceContext));
            services[type.key].push(serviceFactory(serviceContext));
          },
          registerView(path: string, component: Component, accessPatterns?: string[]) {

            log.info(`Registering '${app.id}@${app.version}' view: `, path, component);
            const fullPath = `/apps/${app.id}/${path}`
            viewRepository.registerView(
              fullPath,
              AppViewContext,
              accessPatterns,
              {
                appContext,
                component
              }
            )
            return {
              fullPath
            }
          },
          registerWorkspaceMenuItem(item: WorkspaceMenuItem) {
            log.info(`Registering '${app.id}@${app.version}' workspace menu item: `, item.folderPath, item.label);
            navHubSections.push({
              id: item.folderPath,
              items: [{
                label: item.label,
                action: context => {
                  item.action({
                    ...context,
                    open(viewRoute: string, label: string) {
                      context.openView(viewRoute, {})
                    }
                  })
                },
                accessPatterns: item.accessPatterns
              }]
            });
          },
          registerNavHubSection(section: NavHubSection) {
            log.info(`Registering '${app.id}@${app.version}' NavHub section: `, section.id, section.items.map(r => r.label));
            navHubSections.push(section)
          },
          ...hooks
        };

        try {
          const moduleBlob = await fileResponse.blob();
          const moduleUrl = URL.createObjectURL(moduleBlob);

          const res = await import(/* @vite-ignore */moduleUrl);
          await res.default(portal);
        }
        catch (e) {
          log.err(`Unable to load app '${app.id}@${app.version}': `, e);
          failed = true;
        }
      }
    }

    if (!failed) {
      log.info(`Loaded app ${app.id}@${app.version}`);
    }
  }
} else {
  log.err('Unable to load apps: ', res.status, res.statusText);
}

provide(appsKey, {
  getComponentsFor<T>(slot: apps.AppSlot<T>, appId?: string) {
    const forSlot = components[slot.key] || []
    if (appId === undefined) {
      return forSlot
    }
    return forSlot.filter((c) => c.appContext.appId === appId)
  },

  getServices<T>(type: apps.AppService<T>) {
    return (services[type.key] || []) as T[]
  },
  async onLegacyOrderSaved(order: LegacyOrder, changes: LegacyOrderDraftChanges[]) {
    for (const cb of onLegacyOrderSaved) {
      await cb(order, changes)
    }
  },
  getNavHubSections() {
    return navHubSections
  }
});

</script>

<template>
  <slot></slot>
</template>